/**
 * 时间戳处理工具函数
 * 从 MainPage.tsx 提取，用于减少主文件大小
 */

/**
 * 转换各种时间戳格式为数字（秒）
 */
export const convertTimestampToNumber = (timestamp: any): number => {
  if (!timestamp) return 0;
  
  if (typeof timestamp === 'number') {
    return timestamp;
  }
  
  // 处理 Firestore Timestamp 对象
  if (timestamp && typeof timestamp === 'object') {
    // Firestore Timestamp 格式：{ _seconds, _nanoseconds } 或 { seconds, nanoseconds }
    if (timestamp._seconds !== undefined) {
      return timestamp._seconds + (timestamp._nanoseconds || 0) / 1000000000;
    }
    if (timestamp.seconds !== undefined) {
      return timestamp.seconds + (timestamp.nanoseconds || 0) / 1000000000;
    }
    // 尝试调用 toDate 方法
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().getTime() / 1000;
    }
  }
  
  // 尝试直接转换为数字
  const num = Number(timestamp);
  return isNaN(num) ? 0 : num;
};

/**
 * 验证 timecode 格式是否有效
 */
export const validateTimecode = (timecode: string): boolean => {
  if (!timecode || typeof timecode !== 'string') {
    return false;
  }
  
  // 标准格式：HH:MM:SS FF 或 HH:MM:SS:FF
  const timecodePattern = /^(\d{2}):(\d{2}):(\d{2})[\s:](\d{2})$/;
  const match = timecode.match(timecodePattern);
  
  if (!match) {
    return false;
  }
  
  const [_, hours, minutes, seconds, frames] = match;
  
  // 验证范围
  const h = parseInt(hours, 10);
  const m = parseInt(minutes, 10);
  const s = parseInt(seconds, 10);
  const f = parseInt(frames, 10);
  
  // 小时：0-999（支持长时间直播）
  // 分钟：0-59
  // 秒：0-59
  // 帧：0-29（假设 30fps）
  if (h < 0 || h > 999 || m < 0 || m > 59 || s < 0 || s > 59 || f < 0 || f > 29) {
    return false;
  }
  
  return true;
};

/**
 * 将 timecode 转换为秒数
 */
export const timecodeToSeconds = (timecode: string): number => {
  if (!validateTimecode(timecode)) {
    return 0;
  }
  
  const parts = timecode.split(/[\s:]/);
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(parts[2], 10);
  const frames = parseInt(parts[3], 10);
  
  return hours * 3600 + minutes * 60 + seconds + frames / 30;
};

/**
 * 将秒数转换为 timecode
 */
export const secondsToTimecode = (seconds: number, fps: number = 30): string => {
  const MAX_VIDEO_DURATION = 1000000; // 约 278 小时
  
  if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0 || seconds > MAX_VIDEO_DURATION) {
    return '00:00:00 00';
  }
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const frames = Math.floor((seconds % 1) * fps);
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')} ${frames.toString().padStart(2, '0')}`;
};

/**
 * 计算帧数（从时间的小数部分）
 */
export const calculateFrameFromTime = (time: number, fps: number = 30): number => {
  return Math.floor((time % 1) * fps);
};

